import { type FC, type JSX } from 'react'
import { FormattedMessage } from 'react-intl'

import { useGlobalConfigContext } from '../../../../../global-config'
import { TENANT } from '../../../../../tenant'
import { productAccessorIsPrescriptionProduct } from '../../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { useProductPageContext } from '../../../productPageContext'
import { ProductHeadGenericLabel } from '../ProductHeadGenericLabel/ProductHeadGenericLabel'
import { useProductHeadLabels } from '../useProductHeadLabels'

import { ProductHeadNowLabel } from './ProductHeadNowLabel/ProductHeadNowLabel'
import { ProductHeadPrescriptionRequiredLabel } from './ProductHeadPrescriptionRequiredLabel'
import { ProductHeadPromotionLabelRenderer } from './ProductHeadPromotionLabelRenderer/ProductHeadPromotionLabelRenderer'
import { ProductHeadRedPointsLabel } from './ProductHeadRedPointsLabel/ProductHeadRedPointsLabel'
import { ProductHeadSellerPromotionLabel } from './ProductHeadSellerPromotionLabel/ProductHeadSellerPromotionLabel'

export const ProductHeadLabelRenderer: FC = () => {
  const { tenant } = useGlobalConfigContext()
  const { currentOffer, currentVariant, nowDetails } = useProductPageContext()
  const productHeadLabels = useProductHeadLabels({
    offer: currentOffer,
    product: currentVariant,
  })

  const isPrescriptionProduct =
    productAccessorIsPrescriptionProduct(currentVariant)

  const isPrescriptionProductAndTenantIsCh =
    tenant === TENANT.CH && isPrescriptionProduct

  const productLabel = (labelType: string): JSX.Element | null => {
    return isPrescriptionProduct ? null : (
      <ProductHeadNowLabel key={labelType} />
    )
  }

  return (
    <div className="mb-2.5 flex flex-wrap items-center">
      {productHeadLabels.map((label) => {
        switch (label.type) {
          case 'now': {
            return nowDetails.nowCustomerJourneyEnabled ? (
              <ProductHeadGenericLabel
                className="border-dark-primary-min bg-light-primary-low"
                key={label.type}
              >
                <FormattedMessage id="product.label.nowProductGroup" />
              </ProductHeadGenericLabel>
            ) : (
              productLabel(label.type)
            )
          }
          case 'outOfSale': {
            if (isPrescriptionProductAndTenantIsCh) {
              return null
            }

            return (
              <ProductHeadGenericLabel
                className="border-0 bg-dark-redpoints text-light-primary-strong"
                key={label.type}
              >
                <FormattedMessage id="general.label.product.outOfTrade" />
              </ProductHeadGenericLabel>
            )
          }
          case 'prescription_medicine': {
            if (isPrescriptionProductAndTenantIsCh) {
              return (
                <ProductHeadGenericLabel
                  className="border-light-primary-medium bg-light-secondary-medium capitalize"
                  key={label.type}
                >
                  {label.labelText}
                </ProductHeadGenericLabel>
              )
            }

            return (
              <ProductHeadPrescriptionRequiredLabel
                key={label.type}
                label={label}
              />
            )
          }
          case 'promotion': {
            return (
              <ProductHeadPromotionLabelRenderer
                data-qa-id="aktion-label"
                key={label.type}
                label={label}
              />
            )
          }
          case 'promotionSet': {
            return (
              <ProductHeadGenericLabel
                className="border-dark-redpoints bg-light-primary-low text-dark-redpoints"
                data-qa-id="sparset-label"
                key={label.type}
              >
                <FormattedMessage id="general.label.product.bundle" />
              </ProductHeadGenericLabel>
            )
          }
          case 'redPointsMultiplier': {
            return <ProductHeadRedPointsLabel key={label.type} label={label} />
          }
          case 'sellerPromotion': {
            return (
              <ProductHeadSellerPromotionLabel
                key={label.type}
                offer={currentOffer}
              />
            )
          }
          default: {
            return (
              <ProductHeadGenericLabel
                className="border-dark-primary-min bg-light-primary-low"
                key={label.type}
              >
                {label.labelText}
              </ProductHeadGenericLabel>
            )
          }
        }
      })}
      <hr className="mb-2 hidden h-0.5 grow border-0 border-b border-dark-primary-min tablet:flex" />
    </div>
  )
}
